import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

export interface MasterDetailState {
  area: 'socios' | 'solicitudes' | 'historial' | null;
  nuevo?: boolean;
  partyUuid?: string | null;
  view?: 'siniestro' | 'beneficio' | 'incorporacion' | 'transferencia' | 'baja' | null;
  claimUuid?: string | null;
  requestUuid?: string | null;
  dano?: boolean;
  detailUuid?: string | null;
}

@Injectable({ providedIn: 'root' })
export class MasterDetailNav {
  private router = inject(Router);
  private location = inject(Location);

  readonly urlChanges$ = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map(() => this.parse()),
    startWith(this.parse())
  );

  parse(url = this.router.url): MasterDetailState {
    const parts = url.split('?')[0].split('/').filter(Boolean);
    if (parts[0] === 'socios') {
      if (parts[1] === 'nuevo') return { area: 'socios', nuevo: true };
      const partyUuid = parts[1] || null;
      const view = (parts[2] === 'siniestro' || parts[2] === 'beneficio' || parts[2] === 'incorporacion' || parts[2] === 'transferencia' || parts[2] === 'baja')
        ? parts[2] as MasterDetailState['view']
        : null;
      let claimUuid: string | null = null;
      let dano = false;
      let detailUuid: string | null = null;
      if (view === 'siniestro') {
        claimUuid = parts[3] && parts[3] !== 'dano' ? parts[3] : 'nuevo';
        if (parts[3] === 'dano') {
          dano = true;
          detailUuid = parts[4] || null;
        } else if (parts[4] === 'dano') {
          dano = true;
          detailUuid = parts[5] || null;
        }
      }
      return { area: 'socios', partyUuid, view, claimUuid, dano, detailUuid };
    }
    if (parts[0] === 'solicitudes') {
      if (parts[1] === 'historial') return { area: 'historial' };
      const requestUuid = parts[1] || null;
      const dano = parts[2] === 'dano';
      const detailUuid = parts[3] || null;
      return { area: 'solicitudes', requestUuid, dano, detailUuid };
    }
    return { area: null };
  }

  go(commands: any[], extras?: NavigationExtras) {
    return this.router.navigate(commands, extras);
  }

  replace(commands: any[], extras?: NavigationExtras) {
    return this.router.navigate(commands, { ...extras, replaceUrl: true });
  }

  /** Atrás de hardware/header: sale del detalle y vuelve al maestro. */
  back(fallback?: any[]) {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this.location.back();
      return;
    }
    if (fallback) this.router.navigate(fallback);
  }

  party(partyUuid: string) {
    return ['/socios', partyUuid];
  }

  newParty() {
    return ['/socios', 'nuevo'];
  }

  claim(partyUuid: string, claimUuid = 'nuevo') {
    return ['/socios', partyUuid, 'siniestro', claimUuid || 'nuevo'];
  }

  damage(partyUuid: string, claimUuid = 'nuevo', detailUuid?: string | null) {
    const base = ['/socios', partyUuid, 'siniestro', claimUuid || 'nuevo', 'dano'];
    return detailUuid ? [...base, detailUuid] : base;
  }

  benefit(partyUuid: string) {
    return ['/socios', partyUuid, 'beneficio'];
  }

  transfer(partyUuid: string) {
    return ['/socios', partyUuid, 'transferencia'];
  }

  baja(partyUuid: string) {
    return ['/socios', partyUuid, 'baja'];
  }

  membership(partyUuid: string) {
    return ['/socios', partyUuid, 'incorporacion'];
  }

  request(uuid: string) {
    return ['/solicitudes', uuid];
  }

  requestDamage(uuid: string, detailUuid?: string | null) {
    const base = ['/solicitudes', uuid, 'dano'];
    return detailUuid ? [...base, detailUuid] : base;
  }

  inbox() {
    return ['/solicitudes'];
  }

  item(basePath: string, id: string | number) {
    return ['/' + this.norm(basePath), String(id)];
  }

  list(basePath: string) {
    return ['/' + this.norm(basePath)];
  }

  detailSegment(basePath: string, url = this.router.url): string | null {
    const path = url.split('?')[0].replace(/\/+$/, '');
    const base = '/' + this.norm(basePath);
    if (path === base) return null;
    if (!path.startsWith(base + '/')) return null;
    return decodeURIComponent(path.slice(base.length + 1).split('/')[0] || '') || null;
  }

  private norm(basePath: string) {
    return String(basePath || '').replace(/^\/+|\/+$/g, '');
  }

  parties() {
    return ['/socios'];
  }
}
